import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import ConfirmDialog from './ConfirmDialog';
import {
  CreditCardIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowTopRightOnSquareIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

// Renders one Stripe Connect account (either the organization's or the signed-in
// user's personal/coach account) and manages its own connect/status/disconnect state.
const StripeAccountPanel = ({ title, description, scope, organizationId }) => {
  const [accountStatus, setAccountStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [confirmState, setConfirmState] = useState(null);

  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    params.set('scope', scope);
    if (scope === 'organization' && organizationId) {
      params.set('organization_id', organizationId);
    }
    return params;
  }, [scope, organizationId]);

  const fetchAccountStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = buildParams();
      const response = await fetch(`/api/stripe-accounts/connect/status?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAccountStatus(data);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to fetch account status');
      }
    } catch (err) {
      console.error('Error fetching Stripe Connect status:', err);
      setError('Failed to fetch account status');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchAccountStatus();

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('stripe_refresh') === 'true') {
      fetchAccountStatus();
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [fetchAccountStatus]);

  const handleConnect = async () => {
    setConnecting(true);
    setError(null);
    setSuccess(null);

    try {
      const params = buildParams();
      const response = await fetch(`/api/stripe-accounts/connect/authorize?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to initiate Stripe Connect');
        setConnecting(false);
      }
    } catch (err) {
      console.error('Error connecting Stripe:', err);
      setError('Failed to connect Stripe account');
      setConnecting(false);
    }
  };

  const handleOpenDashboard = async () => {
    try {
      const response = await fetch('/api/stripe-accounts/connect/login-link', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          scope,
          ...(scope === 'organization' && organizationId ? { organization_id: organizationId } : {})
        })
      });

      if (response.ok) {
        const data = await response.json();
        window.open(data.url, '_blank');
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to open Stripe dashboard');
      }
    } catch (err) {
      console.error('Error opening dashboard:', err);
      setError('Failed to open Stripe dashboard');
    }
  };

  const handleDisconnect = () => {
    setConfirmState({
      title: 'Disconnect Stripe Account',
      message: 'Are you sure you want to disconnect this Stripe account? You will no longer be able to accept payments through it.',
      isDangerous: true,
      confirmLabel: 'Disconnect',
      onConfirm: async () => {
        try {
          const params = buildParams();
          const response = await fetch(`/api/stripe-accounts/connect/disconnect?${params.toString()}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });

          if (response.ok) {
            setAccountStatus({ connected: false, ready: false });
            setSuccess('Stripe account disconnected successfully');
          } else {
            const errorData = await response.json();
            setError(errorData.message || 'Failed to disconnect account');
          }
        } catch (err) {
          console.error('Error disconnecting Stripe:', err);
          setError('Failed to disconnect Stripe account');
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center">
          <CreditCardIcon className="h-6 w-6 text-indigo-600 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        </div>
        {accountStatus?.environment && (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
              accountStatus.environment === 'live' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
            }`}
            title={accountStatus.environment === 'live' ? 'Real payments' : 'Test payments only - no real money moves'}
          >
            {accountStatus.environment === 'live' ? 'LIVE' : 'TEST MODE'}
          </span>
        )}
      </div>

      <p className="text-sm text-gray-600 mb-4">{description}</p>
      {accountStatus?.environment === 'live' && !accountStatus?.connected && (
        <p className="text-sm text-amber-700 mb-4">
          Your organization has been switched to live payments. Connect Stripe below with your real business details to start accepting payments.
        </p>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
          <span className="text-sm text-red-800">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <XMarkIcon className="h-5 w-5 text-red-600" />
          </button>
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center">
          <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
          <span className="text-sm text-green-800">{success}</span>
          <button onClick={() => setSuccess(null)} className="ml-auto">
            <XMarkIcon className="h-5 w-5 text-green-600" />
          </button>
        </div>
      )}

      {!accountStatus?.connected ? (
        <button
          onClick={handleConnect}
          disabled={connecting}
          className="w-full sm:w-auto px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {connecting ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Connecting...
            </>
          ) : (
            <>
              <CreditCardIcon className="h-5 w-5 mr-2" />
              Connect Stripe Account
            </>
          )}
        </button>
      ) : (
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center mb-2">
                {accountStatus.ready ? (
                  <>
                    <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                    <span className="text-sm font-medium text-green-800">Account Ready</span>
                  </>
                ) : (
                  <>
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2" />
                    <span className="text-sm font-medium text-yellow-800">Setup Incomplete</span>
                  </>
                )}
              </div>

              {accountStatus.account && (
                <div className="text-sm text-gray-600 space-y-1">
                  {accountStatus.account.business_name && (
                    <p><span className="font-medium">Business:</span> {accountStatus.account.business_name}</p>
                  )}
                  {accountStatus.account.email && (
                    <p><span className="font-medium">Email:</span> {accountStatus.account.email}</p>
                  )}
                  <p><span className="font-medium">Country:</span> {accountStatus.account.country?.toUpperCase() || 'US'}</p>
                  <div className="flex items-center space-x-4 mt-2">
                    <span className={`text-xs px-2 py-1 rounded ${accountStatus.account.charges_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {accountStatus.account.charges_enabled ? 'Charges Enabled' : 'Charges Pending'}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded ${accountStatus.account.payouts_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {accountStatus.account.payouts_enabled ? 'Payouts Enabled' : 'Payouts Pending'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
            {!accountStatus.ready && (
              <button
                onClick={handleConnect}
                disabled={connecting}
                className="px-4 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
              >
                Complete Setup
              </button>
            )}

            {accountStatus.ready && (
              <button
                onClick={handleOpenDashboard}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 flex items-center"
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-2" />
                Open Stripe Dashboard
              </button>
            )}

            <button
              onClick={handleDisconnect}
              className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100"
            >
              Disconnect
            </button>
          </div>
        </div>
      )}
      <ConfirmDialog
        isOpen={!!confirmState}
        title={confirmState?.title}
        message={confirmState?.message}
        isDangerous={confirmState?.isDangerous}
        confirmLabel={confirmState?.confirmLabel}
        onConfirm={() => { confirmState.onConfirm(); setConfirmState(null); }}
        onCancel={() => setConfirmState(null)}
      />
    </div>
  );
};

// A user who is both an org admin and a coach can connect both an organization
// payout account and their own personal one at the same time - they aren't
// mutually exclusive, so each gets its own independent panel rather than a toggle.
// Org admins choose who pays card processing fees. The platform keeps none of it.
const ProcessingFeeSetting = ({ organizationId }) => {
  const [payerCoversFees, setPayerCoversFees] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/organizations/${organizationId}/payment-settings`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    })
      .then(res => (res.ok ? res.json() : null))
      .then(data => { if (!cancelled && data) setPayerCoversFees(data.payerCoversFees); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [organizationId]);

  const save = async (value) => {
    setSaving(true);
    try {
      const res = await fetch(`/api/organizations/${organizationId}/payment-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ payerCoversFees: value })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to save');
      setPayerCoversFees(data.payerCoversFees);
      toast.success(value ? 'Payers will cover processing fees' : 'Your organization will cover processing fees');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (payerCoversFees === null) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-medium text-gray-900">Card Processing Fees</h3>
      <p className="text-sm text-gray-600 mt-1 mb-4">
        Stripe charges about 2.9% + 30¢ per card payment. Choose who pays it.
      </p>
      <div className="space-y-3">
        {[
          { value: true, title: 'Payer covers the fee', detail: 'The fee is added at checkout, so you receive your full price. Example: a $40.00 lesson costs the payer $41.51.' },
          { value: false, title: 'Organization covers the fee', detail: 'Payers pay your listed price and the fee comes out of your payout. Example: you receive $38.54 from a $40.00 lesson.' }
        ].map(option => (
          <label
            key={String(option.value)}
            className={`flex items-start gap-3 border rounded-lg px-4 py-3 cursor-pointer ${
              payerCoversFees === option.value ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-indigo-300'
            }`}
          >
            <input
              type="radio"
              name="processing-fee-setting"
              className="mt-1"
              checked={payerCoversFees === option.value}
              disabled={saving}
              onChange={() => save(option.value)}
            />
            <div>
              <div className="text-sm font-semibold text-gray-900">{option.title}</div>
              <div className="text-xs text-gray-600 mt-0.5">{option.detail}</div>
            </div>
          </label>
        ))}
      </div>
      <p className="text-xs text-gray-500 mt-3">Donors can always choose whether to cover the fee on donations.</p>
    </div>
  );
};

const StripeConnectSettings = () => {
  const { user } = useAuth();
  const [organizationId, setOrganizationId] = useState(null);
  const [isCoach, setIsCoach] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const fetchOrganizations = async () => {
      if (user?.role !== 'org_admin') return null;
      try {
        const response = await fetch('/api/organizations/my-organizations', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          const firstOrgId = data?.[0]?.organization_id || data?.[0]?.Organization?.id || null;
          setOrganizationId(firstOrgId);
        }
      } catch (error) {
        console.error('Error fetching organizations:', error);
      }
      return null;
    };

    const fetchTeams = async () => {
      try {
        const response = await fetch('/api/teams/my-teams', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          setIsCoach(Array.isArray(data) && data.length > 0);
        }
      } catch (error) {
        console.error('Error fetching teams:', error);
      }
    };

    Promise.all([fetchOrganizations(), fetchTeams()]).finally(() => setReady(true));
  }, [user]);

  if (!ready) return null;

  const isOrgAdmin = user?.role === 'org_admin' && !!organizationId;

  if (!isOrgAdmin && !isCoach) return null;

  return (
    <div className="space-y-6">
      {isOrgAdmin && (
        <StripeAccountPanel
          title="Organization Payouts"
          description="Connect Stripe to receive payments for invoices billed by your organization."
          scope="organization"
          organizationId={organizationId}
        />
      )}
      {isOrgAdmin && <ProcessingFeeSetting organizationId={organizationId} />}
      {isCoach && (
        <StripeAccountPanel
          title="My Coach Payouts"
          description="Connect your own Stripe account to receive payments directly for invoices you personally send to players and parents."
          scope="coach"
        />
      )}
    </div>
  );
};

export default StripeConnectSettings;
